import {
  RefundPriceEstimator,
  isRefundProduct,
  CommercialTermsPriceEstimator,
  isCommercialTermsProduct,
  isOneTimeProduct,
  isUsageProduct,
  isFixedDeductionProductOnly,
  isPercentageDeductionProductOnly,
  isCommissionProduct,
  UsageProductPriceEstimator,
  SubscriptionPriceEstimator,
  isSubscriptionProduct,
  FixedOneTimePriceEstimator,
  resolveEffectiveProducts,
} from '@vy/services';
import type { Product, Plan } from '@vy/types';
import { isNullOrUndefined, raise, sumBy } from '@vy/utils';

export function calculateEstimatedPlanValue(plan: Plan) {
  const effectiveProducts = resolveEffectiveProducts(plan.products ?? []);

  return sumBy(effectiveProducts, (product) => calculateProductPrice(plan, product));
}

function calculateProductPrice(plan: Plan, product: Product) {
  const variant = product.variants[0];

  if (isNullOrUndefined(variant?.pricing)) {
    return 0;
  }

  const factorizationPricingModel = variant.pricing.pricingModel;

  if (isOneTimeProduct(product)) {
    return new FixedOneTimePriceEstimator().estimate(product, undefined);
  }

  if (isSubscriptionProduct(product)) {
    return new SubscriptionPriceEstimator().estimate(product, plan.billingData);
  }

  if (isUsageProduct(product)) {
    return new UsageProductPriceEstimator().estimate(product, plan.billingData.billingInterval);
  }

  if (isCommercialTermsProduct(product)) {
    return new CommercialTermsPriceEstimator().estimate(product, undefined);
  }

  if (isCommissionProduct(product) || isPercentageDeductionProductOnly(product)) {
    return 0;
  }

  if (isRefundProduct(product)) {
    return new RefundPriceEstimator().estimate(product, undefined);
  }

  if (isFixedDeductionProductOnly(product)) {
    return -product.variants[0].pricing.priceConfiguration.amountToDeduct;
  }

  return raise(`Unsupported pricing model: ${factorizationPricingModel}`);
}
